import React, { useMemo } from "react";
import moment from "moment";

import { TrendLineChart } from "@/_self_managed/SM_Dashboard/LensesV2/comps/TrendLineChart";
import { useSignalCategories } from "./SignalCategoriesContext";

// The category render order for a cluster card — the bar's segments, its
// legend, and these sparklines all draw from the tenant's category list
// (useSignalCategories().list, in the order the BE serves it), so they
// can't drift apart.

/**
 * Per-signal-category trend lines for one cluster card — the LensesV2
 * over-time idea (period → interval → month/week/day buckets) applied to the
 * card's own observations, all categories on ONE chart under the category bar
 * so their shapes can be read against each other (a card where satisfaction
 * falls as dissatisfaction climbs is a story four separate cells can't tell).
 *
 * Entirely client-side: observations already carry dateThatMatters, the
 * selected period carries the interval the BE computed for its range
 * (intervalForRange: ≤14d day, ≤90d week, else month), and the bucket passed
 * in is the ALREADY-NARROWED one, so the lines agree with the counts and bar
 * above them.
 *
 * Time keys are formatted to match TrendLineChart's x-tick parsers exactly
 * (month "2026-01", week "2026-W07" ISO, day "2026-01-15").
 */

const INTERVAL_UNIT = { day: "day", week: "isoWeek", month: "month" };
const INTERVAL_STEP = { day: "day", week: "week", month: "month" };
const INTERVAL_KEY_FORMAT = { day: "YYYY-MM-DD", week: "GGGG-[W]WW", month: "YYYY-MM" };

// Degenerate guard — a pathological range/interval combo shouldn't render
// hundreds of bins into a 150px sparkline.
const MAX_BINS = 120;

const allObservationsOf = (bucket) => {
  const rows = bucket.children?.length ? bucket.children : [bucket];
  return [
    ...rows.flatMap((r) => r.observations || []),
    ...(bucket.parkedObservations || []),
  ];
};

// The period's time bins as formatted keys, or null when the period can't
// chart (no range, unknown interval, degenerate bin count).
export const binsForPeriod = (period) => {
  const interval = period?.interval;
  if (!period?.from || !period?.to || !INTERVAL_KEY_FORMAT[interval]) return null;
  const fmt = INTERVAL_KEY_FORMAT[interval];
  const bins = [];
  const cursor = moment(period.from).startOf(INTERVAL_UNIT[interval]);
  const end = moment(period.to);
  while (cursor.isSameOrBefore(end) && bins.length <= MAX_BINS) {
    bins.push(cursor.format(fmt));
    cursor.add(1, INTERVAL_STEP[interval]);
  }
  return bins.length < 2 || bins.length > MAX_BINS ? null : { bins, fmt };
};

// One minimal line over one set of observations — the finding cards' and
// the donut panel's line, coloured by the node's signal category. `opacity`
// is the resting stroke opacity: the finding cards pass the chart's dimmed
// value so the line reads as a hint under the card's text rather than a
// chart (hovering brings it up to full); the donut panel keeps it solid.
// `countsByBin` ({ binKey: n }, keys in the period's interval format) is the
// pre-binned alternative to `observations` — the Topics V2 tab gets its
// series from the time-series endpoint and never holds observations.
export const ObservationSparkline = ({ observations, countsByBin, period, color, label = "count", height = 48, opacity = 1, onBinClick }) => {
  const data = useMemo(() => {
    const binned = binsForPeriod(period);
    if (!binned) return null;
    const counts = countsByBin ? { ...countsByBin } : {};
    if (!countsByBin) {
      for (const o of observations || []) {
        if (!o.dateThatMatters) continue;
        const key = moment(o.dateThatMatters).format(binned.fmt);
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    if (!Object.values(counts).some((n) => n > 0)) return null;
    return binned.bins.map((key) => ({
      timeInterval: key,
      dimensions: [{ value: label, display: label, count: counts[key] || 0 }],
    }));
  }, [observations, countsByBin, period, label]);

  if (!data) return null;
  return (
    <div style={{ width: "100%" }}>
      <TrendLineChart
        data={data}
        interval={period.interval}
        height={height}
        colors={{ [label]: color }}
        defaultOpacity={opacity}
        hideXAxisTicks
        hideGrid
        margin={{ top: 6, right: 4, bottom: 6, left: 4 }}
        {...(onBinClick ? { onXClick: (x) => onBinClick(String(x)) } : {})}
      />
    </div>
  );
};

// `emphasisCategory` is the same leading category the bar above emphasises:
// its line draws at full colour, the rest sit at the chart's hover-dim
// opacity until hovered, and it leads the draw + tooltip order.
// `countsByCategory` ({ category: { binKey: n } }) is the pre-binned
// alternative to walking the bucket's observations — see ObservationSparkline.
export const CategorySparklines = ({ bucket, signalMetaById, period, emphasisCategory = null, countsByCategory: preBinned = null }) => {
  const categoryTheme = useSignalCategories();
  const series = useMemo(() => {
    const binned = binsForPeriod(period);
    if (!binned) return null;
    const { bins, fmt } = binned;

    // category → { binKey → count } over every observation on the card.
    const countsByCategory = preBinned ? preBinned : {};
    if (!preBinned) {
      for (const o of allObservationsOf(bucket)) {
        const category = o.signalId ? signalMetaById[String(o.signalId)]?.category : null;
        if (!category || !o.dateThatMatters) continue;
        const key = moment(o.dateThatMatters).format(fmt);
        (countsByCategory[category] ||= {})[key] = (countsByCategory[category][key] || 0) + 1;
      }
    }

    // Only categories the card actually carries become lines — an absent
    // category must not draw a flat zero line competing with the real ones.
    const present = categoryTheme.list
      .filter((c) => countsByCategory[c.value] && Object.values(countsByCategory[c.value]).some((n) => n > 0))
      .map((c) => ({ category: c.value, title: c.display }))
      .sort((a, b) => (b.category === emphasisCategory) - (a.category === emphasisCategory));
    if (!present.length) return null;

    // TrendLineChart draws one line per entry in a bucket's `dimensions`, so
    // every category rides in the SAME bucket to land on one chart. Its
    // `colors` map is keyed by `display`.
    return {
      data: bins.map((key) => ({
        timeInterval: key,
        dimensions: present.map((seg) => ({
          value: seg.category,
          display: seg.title,
          count: countsByCategory[seg.category][key] || 0,
        })),
      })),
      colors: Object.fromEntries(
        present.map((seg) => [seg.title, categoryTheme.accentOf(seg.category)])
      ),
      // LineChart matches highlightLine against the pivoted dataKey, which
      // is `${value}_count`, not the display name.
      highlight: present.some((seg) => seg.category === emphasisCategory)
        ? `${emphasisCategory}_count`
        : null,
    };
  }, [bucket, signalMetaById, period, emphasisCategory, categoryTheme, preBinned]);

  if (!series) return null;

  // No legend of its own: identification rides on the category bar directly
  // above (same order, same hue family), and hovering a line names it.
  return (
    <TrendLineChart
      data={series.data}
      interval={period.interval}
      height={80}
      colors={series.colors}
      highlight={series.highlight}
      highlightStrokeBoost={0}
      defaultOpacity={1}
      hideXAxisTicks
      hideGrid
      margin={{ top: 6, right: 4, bottom: 6, left: 4 }}
    />
  );
};
